/**
 * Manifest-backed Control Bridge (design handoff Phase 3).
 * Routes: /render/control-bridge · /render/:workspaceSlug/control-bridge
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Spin, Typography } from 'antd';
import { ControlBridgeShell } from './ControlBridgeShell';
import { isKryspaceRenderServiceError, fetchRenderManifestWithMeta } from '../../services/kryspaceRenderService';
import {
  controlBridgeLoadErrorMessage,
  loadControlBridgeWorkspace,
} from '../../utils/controlBridgeLoad';
import type { KryspaceRenderManifest } from '../../types/kryspaceRenderManifest';
import type { KryspaceControlBridgeStructure } from '../../types/kryspaceControlBridgeStructure';
import { RENDER_SURFACE_DEV_MOCK } from '../../config';
import { useUser } from '../../contexts/UserContext';
import { companyModelReadinessService, type CompanyModelReadiness } from '../../services/companyModelReadinessService';
import { SELECTED_COMPANY_KRYSPACE_SLUG_KEY } from '../../config/kryspaceReadSource';
import { resolveRenderSurfaceSlugSession, controlBridgeWorkspaceSlug } from '../../utils/renderSurfaceSlugSession';
import { CONTROL_BRIDGE_HOME_PATH } from '../../utils/controlBridgeRoutes';
import { POST_REGISTER_PATH } from '../../utils/onboardingNavigation';
import { companySelectionAlertDescription } from '../../utils/companySelectionHelp';
import { useCanonicalRouteOnCompanySwitch } from '../../hooks/useCanonicalRouteOnCompanySwitch';
import { RenderSurfaceBindingSubtitle } from '../RenderSurface/RenderSurfaceBindingSubtitle';
import styles from './ControlBridge.module.css';
import { ControlBridgeSlowLoadHint } from './ControlBridgeSlowLoadHint';
import { subscribeControlBridgeModelChanged } from '../../utils/chatWorkspaceToolInvocation';

const { Paragraph } = Typography;

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ready';
      manifest: KryspaceRenderManifest;
      structure: KryspaceControlBridgeStructure;
      devMock: boolean;
      structureDevMock: boolean;
      structureUnavailable?: boolean;
    }
  | { status: 'error'; message: string; code?: string; httpStatus?: number };

function friendlyControlBridgeError(message: string, code?: string, err?: unknown): string {
  if (err !== undefined) {
    const mapped = controlBridgeLoadErrorMessage(err, code);
    if (mapped) return mapped;
  }
  const lower = message.toLowerCase();
  if (
    code === 'NOT_CONFIGURED' ||
    lower.includes('global_slug_registry') ||
    (lower.includes('kryspace') && lower.includes('unavailable'))
  ) {
    return 'This company does not have a Kryspace workspace provisioned yet. Finish Discovery intake or set companies.kryspace_slug before opening Control Bridge.';
  }
  if (code === 'UNKNOWN_SLUG' || lower.includes('unknown slug')) {
    return 'No Kryspace workspace is registered for this company yet. Continue Discovery intake or contact support to provision the workspace.';
  }
  return message;
}

export const ControlBridgePage: React.FC = () => {
  const { workspaceSlug: routeSlug } = useParams<{ workspaceSlug?: string }>();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated,
    isLoading: userLoading,
    selectedCompany,
    availableCompanies,
    loadUserCompanies,
  } = useUser();
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>({ status: 'idle' });
  const [modelReadiness, setModelReadiness] = useState<CompanyModelReadiness | null>(null);

  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;
    void (async () => {
      setCompaniesLoaded(false);
      try {
        await loadUserCompanies();
      } finally {
        setCompaniesLoaded(true);
      }
    })();
  }, [user?.id, isAuthenticated, loadUserCompanies]);

  const slugSession = useMemo(
    () =>
      resolveRenderSurfaceSlugSession({
        routeSlug,
        selectedCompany,
        availableCompanies,
        userCompanyId: user?.companyId,
        persistedCompanyId:
          typeof localStorage !== 'undefined' ? localStorage.getItem('selectedCompanyId') : null,
        persistedKryspaceSlug:
          typeof localStorage !== 'undefined'
            ? localStorage.getItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY)
            : null,
      }),
    [routeSlug, selectedCompany, availableCompanies, user?.companyId]
  );

  const driveRailRef = useRef<string | undefined>(undefined);
  const drivePeriodRef = useRef<string | undefined>(undefined);

  const {
    workspaceCompanyId,
    companyRecord,
    derivedSlug,
    slugFromRoute,
    requiresCompanyContext,
  } = slugSession;

  // Do not load Control Bridge for name-hint guesses (incomplete intake companies).
  const effectiveSlug = useMemo(
    () => controlBridgeWorkspaceSlug(slugSession, selectedCompany?.kryspace_slug),
    [slugSession, selectedCompany?.kryspace_slug]
  );

  useEffect(() => {
    driveRailRef.current = undefined;
    drivePeriodRef.current = undefined;
  }, [effectiveSlug]);

  useCanonicalRouteOnCompanySwitch(
    slugFromRoute,
    selectedCompany?.id,
    '/render/control-bridge',
    navigate
  );

  const load = useCallback(async () => {
    if (!effectiveSlug) {
      setLoadState({ status: 'idle' });
      return;
    }
    setLoadState((prev) => {
      if (prev.status === 'ready' && prev.manifest.workspaceSlug === effectiveSlug) {
        return prev;
      }
      return { status: 'loading' };
    });
    try {
      const { manifestResult, structureResult } = await loadControlBridgeWorkspace(effectiveSlug, {
        driveFilterScope: driveRailRef.current,
        drivePeriod: drivePeriodRef.current,
      });
      setLoadState({
        status: 'ready',
        manifest: manifestResult.manifest,
        structure: structureResult.structure,
        devMock: manifestResult.devMock,
        structureDevMock: structureResult.devMock,
        structureUnavailable: structureResult.structureUnavailable,
      });
    } catch (err) {
      const code = isKryspaceRenderServiceError(err) ? err.code : undefined;
      const httpStatus = isKryspaceRenderServiceError(err) ? err.httpStatus : undefined;
      const rawMessage = isKryspaceRenderServiceError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to load Control Bridge';
      setLoadState({
        status: 'error',
        message: friendlyControlBridgeError(rawMessage, code, err),
        code,
        httpStatus,
      });
    }
  }, [effectiveSlug]);

  const refreshDriveRail = useCallback(
    async (scope: string, opts?: { drivePeriod?: string }) => {
      if (!effectiveSlug) return;
      const rail = !scope || scope === 'company' ? undefined : scope.trim();
      driveRailRef.current = rail;
      if (opts?.drivePeriod !== undefined) {
        drivePeriodRef.current = opts.drivePeriod.trim() || undefined;
      }
      try {
        const { manifest } = await fetchRenderManifestWithMeta(effectiveSlug, {
          driveFilterScope: rail,
          drivePeriod: drivePeriodRef.current,
        });
        setLoadState((prev) => (prev.status === 'ready' ? { ...prev, manifest } : prev));
      } catch {
        /* SPA still filters FlowEnvelope client-side if rail refetch fails. */
      }
    },
    [effectiveSlug]
  );

  const refreshAfterChatWrite = useCallback(async () => {
    if (!effectiveSlug) return;
    try {
      const { manifestResult, structureResult } = await loadControlBridgeWorkspace(effectiveSlug, {
        driveFilterScope: driveRailRef.current,
        drivePeriod: drivePeriodRef.current,
      });
      setLoadState({
        status: 'ready',
        manifest: manifestResult.manifest,
        structure: structureResult.structure,
        devMock: manifestResult.devMock,
        structureDevMock: structureResult.devMock,
        structureUnavailable: structureResult.structureUnavailable,
      });
    } catch {
      /* Keep the current Drive; Chat already reported the write outcome. */
    }
  }, [effectiveSlug]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => subscribeControlBridgeModelChanged(effectiveSlug, () => void refreshAfterChatWrite()),
    [effectiveSlug, refreshAfterChatWrite]
  );

  useEffect(() => {
    const companyId = selectedCompany?.id ?? workspaceCompanyId;
    if (!companyId || !isAuthenticated) {
      setModelReadiness(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const readiness = await companyModelReadinessService.assess(companyId);
        if (!cancelled) setModelReadiness(readiness);
      } catch {
        if (!cancelled) setModelReadiness(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompany?.id, workspaceCompanyId, isAuthenticated]);

  if (userLoading || !isAuthenticated) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  if (requiresCompanyContext && !workspaceCompanyId) {
    if (!companiesLoaded) {
      return (
        <div className={styles.loading}>
          <Spin size="large" />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Loading your company…
          </Paragraph>
        </div>
      );
    }
    return (
      <div className={styles.errorPanel}>
        <Alert
          type="warning"
          showIcon
          message="Select a company"
          description={companySelectionAlertDescription({
            includeCreateHint: availableCompanies.length === 0,
            hasAvailableCompanies: availableCompanies.length > 0,
          })}
        />
      </div>
    );
  }

  if (!effectiveSlug) {
    return (
      <div className={styles.errorPanel}>
        <Alert
          type="warning"
          showIcon
          message="Workspace not ready yet"
          description="Finish company intake in Discovery chat first. Control Bridge needs a workspace slug after prospector completes."
          action={
            <Button type="primary" size="small" onClick={() => navigate('/onboarding/prospector')}>
              Continue intake
            </Button>
          }
        />
      </div>
    );
  }

  const manifestLink = slugFromRoute ? `/render/${effectiveSlug}` : '/render';
  const companyLabel =
    selectedCompany?.name ?? companyRecord?.name ?? (slugFromRoute ? 'Deep link' : workspaceCompanyId ?? '');

  return (
    <div data-testid="control-bridge-page">
      {modelReadiness &&
      (!modelReadiness.model_ready || !modelReadiness.checks.platform_gates_passed) ? (
        <Alert
          type="warning"
          showIcon
          style={{ margin: '8px 16px 0' }}
          message={
            !modelReadiness.model_ready ? 'Company model not ready' : 'Platform gates not fully passed'
          }
          description={
            <div>
              <p style={{ marginBottom: 8 }}>
                {!modelReadiness.model_ready ? (
                  <>
                    Eligible work queues need imported company-model items in Postgres (slug + non-seed
                    artifact items). Foundation billing or platform-init alone is not enough (ISSUE-268).
                  </>
                ) : (
                  <>
                    Company model is imported. Control Bridge still requires Krysalis Platform
                    initialized and at least one subscription with an ACTIVE operational item.
                    Feature entitlement shells are tracked separately and do not block this banner
                    under the short-term CB gate policy.
                  </>
                )}
              </p>
              <ul style={{ marginBottom: 8 }}>
                {modelReadiness.blockers.map((b) => (
                  <li key={b}>{b.replace(/_/g, ' ')}</li>
                ))}
              </ul>
              <p style={{ marginBottom: 0 }}>
                Company-model items: {modelReadiness.checks.company_model_item_count ?? '—'}; total items:{' '}
                {modelReadiness.checks.artifact_item_count}; platform validation:{' '}
                {modelReadiness.checks.platform_validation_status ?? 'unknown'}.
              </p>
            </div>
          }
        />
      ) : null}
      {process.env.NODE_ENV !== 'production' ? (
      <p className={styles.linkRow} style={{ padding: '8px 16px 0', margin: 0 }}>
        <Link to={manifestLink}>← Raw render manifest</Link>
      </p>
      ) : null}
      {process.env.NODE_ENV !== 'production' ? (
      <RenderSurfaceBindingSubtitle
        className={styles.linkRow}
        style={{ padding: '4px 16px 0', margin: 0, fontSize: 13, color: 'var(--color-text-secondary, #666)' }}
        companyLabel={String(companyLabel)}
        effectiveSlug={effectiveSlug}
        slugFromRoute={slugFromRoute}
        slugSource={derivedSlug?.source}
        derivedSlug={derivedSlug?.slug}
      />
      ) : null}

      {loadState.status === 'ready' && loadState.structureUnavailable ? (
        <Alert
          type="info"
          showIcon
          style={{ margin: '8px 16px' }}
          message="Sidebar structure unavailable"
          description="This workspace has no company-artifact Control Bridge tree yet. Scope filters use company-wide mode; manifest queues still load from kryspace."
        />
      ) : null}

      {loadState.status === 'ready' && (loadState.devMock || loadState.structureDevMock) ? (
        <Alert
          type="info"
          showIcon
          style={{ margin: '8px 16px' }}
          message="Dev mock mode"
          description={
            loadState.devMock && loadState.structureDevMock
              ? 'Manifest and structure from dev mock — not live kryspace read API.'
              : loadState.devMock
                ? 'Manifest from dev mock; structure may be live.'
                : 'Structure from dev mock; manifest may be live.'
          }
        />
      ) : null}

      {loadState.status === 'loading' || loadState.status === 'idle' ? (
        <div className={styles.loading} role="status" data-testid="control-bridge-loading">
          <Spin size="large" />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Building workspace view…
          </Paragraph>
          <ControlBridgeSlowLoadHint active={loadState.status === 'loading'} />
        </div>
      ) : null}

      {loadState.status === 'error' ? (
        <div className={styles.errorPanel} role="alert">
          <p>{loadState.message}</p>
          <Button type="primary" onClick={() => void load()}>
            Retry
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate(CONTROL_BRIDGE_HOME_PATH)}>
            Retry home
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate(POST_REGISTER_PATH)}>
            Continue Discovery
          </Button>
        </div>
      ) : null}

      {loadState.status === 'ready' ? (
        <ControlBridgeShell
          manifest={loadState.manifest}
          structure={loadState.structure}
          devMock={loadState.devMock || loadState.structureDevMock}
          workspaceSlug={effectiveSlug}
          preferredPrimarySlug={selectedCompany?.kryspace_slug ?? null}
          preferredPrimaryTitle={
            selectedCompany?.name ?? companyRecord?.name ?? null
          }
          onManifestRefresh={load}
          onDriveRailChange={refreshDriveRail}
          personalLayerUser={
            user
              ? {
                  id: user.id,
                  label: user.fullName || user.username,
                  username: user.username,
                  email: user.email,
                }
              : undefined
          }
        />
      ) : null}

      {process.env.NODE_ENV === 'development' && RENDER_SURFACE_DEV_MOCK ? (
        <p className={styles.linkRow} style={{ padding: 16 }}>
          REACT_APP_RENDER_SURFACE_DEV_MOCK is enabled.
        </p>
      ) : null}
    </div>
  );
};

export default ControlBridgePage;
